import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReaderBook, ReaderChapter } from '@/services/booksApi';
import { parseChapterFragment } from '@/utils/readerFragments';

export type EndnotesIndexStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface UseEndnotesIndexResult {
  getNote: (noteId: string) => Promise<HTMLElement | null>;
  status: EndnotesIndexStatus;
  error: string | null;
  retry: () => void;
}

interface EndnotesInFlightRequest {
  controller: AbortController;
  promise: Promise<Map<string, HTMLElement>>;
}

const ENDNOTES_FETCH_TIMEOUT_MS = 15_000;
const ENDNOTES_BASENAME = 'endnotes';

class EndnotesRequestError extends Error {
  constructor(message: string, readonly retryable: boolean) {
    super(message);
    this.name = 'EndnotesRequestError';
  }
}

const fetchEndnotesHtml = (chapter: ReaderChapter, controller: AbortController): Promise<string> => {
  let didTimeout = false;
  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, ENDNOTES_FETCH_TIMEOUT_MS);

  return fetch(chapter.htmlUrl, { signal: controller.signal })
    .then((response) => {
      if (!response.ok) {
        const retryable = response.status === 408 || response.status === 429 || response.status >= 500;
        throw new EndnotesRequestError(
          `Failed to fetch endnotes chapter '${chapter.title}': ${response.statusText || response.status}`,
          retryable,
        );
      }
      return response.text();
    })
    .catch((requestError: unknown) => {
      if (didTimeout) {
        throw new EndnotesRequestError(
          `Endnotes request timed out after ${ENDNOTES_FETCH_TIMEOUT_MS / 1000} seconds`,
          true,
        );
      }
      if (requestError instanceof EndnotesRequestError || (requestError instanceof DOMException && requestError.name === 'AbortError')) {
        throw requestError;
      }
      if (requestError instanceof TypeError) {
        throw new EndnotesRequestError(requestError.message || 'Endnotes request failed', true);
      }
      throw requestError;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
    });
};

const buildNoteIndex = (chapter: ReaderChapter, html: string): Map<string, HTMLElement> => {
  const root = parseChapterFragment(html, chapter);
  const index = new Map<string, HTMLElement>();
  root.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
    index.set(element.id, element);
  });
  return index;
};

export const useEndnotesIndex = (readerBook: ReaderBook | null): UseEndnotesIndexResult => {
  const endnotesChapter = readerBook?.chapters.find((chapter) => chapter.basename === ENDNOTES_BASENAME) ?? null;

  const [status, setStatus] = useState<EndnotesIndexStatus>(endnotesChapter ? 'idle' : 'unavailable');
  const [error, setError] = useState<string | null>(null);
  const noteIndexRef = useRef<Map<string, HTMLElement> | null>(null);
  const inFlightRef = useRef<EndnotesInFlightRequest | null>(null);
  const bookIdRef = useRef<string | null>(readerBook?.id ?? null);

  useEffect(() => {
    if (bookIdRef.current !== (readerBook?.id ?? null)) {
      bookIdRef.current = readerBook?.id ?? null;
      noteIndexRef.current = null;
      inFlightRef.current = null;
      setError(null);
      setStatus(endnotesChapter ? 'idle' : 'unavailable');
    }

    return () => {
      const request = inFlightRef.current;
      request?.controller.abort();
      if (inFlightRef.current === request) inFlightRef.current = null;
    };
  }, [readerBook?.id]);

  const load = useCallback((): Promise<Map<string, HTMLElement>> => {
    if (noteIndexRef.current) {
      return Promise.resolve(noteIndexRef.current);
    }
    if (inFlightRef.current) {
      return inFlightRef.current.promise;
    }
    if (!endnotesChapter) {
      return Promise.resolve(new Map());
    }
    setStatus('loading');
    const controller = new AbortController();
    let request: EndnotesInFlightRequest;
    const isActive = () => !controller.signal.aborted && inFlightRef.current === request;
    const promise = fetchEndnotesHtml(endnotesChapter, controller)
      .then((html) => {
        if (!isActive()) throw new DOMException('Endnotes request was aborted', 'AbortError');
        return buildNoteIndex(endnotesChapter, html);
      })
      .then((index) => {
        if (isActive()) {
          noteIndexRef.current = index;
          setStatus('ready');
          setError(null);
        }
        return index;
      })
      .catch((loadError: unknown) => {
        if (inFlightRef.current === request) {
          const message = loadError instanceof Error ? loadError.message : 'Failed to load endnotes';
          setStatus('error');
          setError(message);
        }
        throw loadError;
      })
      .finally(() => {
        if (inFlightRef.current === request) {
          inFlightRef.current = null;
        }
      });
    request = { controller, promise };
    inFlightRef.current = request;
    return promise;
  }, [endnotesChapter]);

  // Throws on fetch/parse failure so callers can distinguish "endnotes chapter
  // failed to load" from "this note id isn't in it" (a null return).
  const getNote = useCallback(
    async (noteId: string): Promise<HTMLElement | null> => {
      if (!endnotesChapter) {
        return null;
      }
      const index = await load();
      return index.get(noteId) ?? null;
    },
    [endnotesChapter, load],
  );

  const retry = useCallback(() => {
    inFlightRef.current?.controller.abort();
    noteIndexRef.current = null;
    inFlightRef.current = null;
    setError(null);
    setStatus(endnotesChapter ? 'idle' : 'unavailable');
  }, [endnotesChapter]);

  // Memoized so callers that depend on the whole result object (e.g. a
  // useCallback dependency array) don't get a new identity -- and therefore
  // don't tear down and rebuild -- on every render that doesn't actually
  // change anything here.
  return useMemo(() => ({ getNote, status, error, retry }), [getNote, status, error, retry]);
};
